(function () {
  'use strict';

  // ---- Shared logic, defined once (previously redefined inside a DOMContentLoaded ----
  // ---- handler that was re-printed inside every product card's inline <script>). ----

  // Function to find variant based on selected options
  function findVariantBySelectedOptions(productCard, productId) {
    // Get product handle
    const colorsContainer = productCard.querySelector('.collection-product-card__colors');
    const secondOptionContainer = productCard.querySelector('.collection-product-card__second-option');
    const productHandle = colorsContainer ? colorsContainer.dataset.productHandle :
                         (secondOptionContainer ? secondOptionContainer.dataset.productHandle : null);

    // Get selected color
    const selectedColorSwatch = productCard.querySelector('.collection-product-card__color-swatch.active');
    const selectedColor = selectedColorSwatch ? selectedColorSwatch.dataset.color : null;

    // Get selected second option
    const selectedSecondOption = productCard.querySelector('.collection-product-card__second-option-value.active');
    const selectedSecondValue = selectedSecondOption ? selectedSecondOption.dataset.optionValue : null;

    console.log('Finding variant for:', { selectedColor, selectedSecondValue, productHandle });

    // If we have both options selected, we need to find the variant that matches BOTH
    if (selectedColor && selectedSecondValue && productHandle) {
      // Fetch product data to find the exact variant match
      return fetchProductVariants(productHandle, selectedColor, selectedSecondValue, selectedColorSwatch.dataset.variantImage);
    } else if (selectedColor) {
      // Only color selected
      return Promise.resolve({
        variantId: selectedColorSwatch.dataset.variantId,
        available: selectedColorSwatch.dataset.variantAvailable === 'true',
        image: selectedColorSwatch.dataset.variantImage,
        price: selectedColorSwatch.dataset.variantPrice,
        compareAtPrice: selectedColorSwatch.dataset.variantComparePrice || null
      });
    } else if (selectedSecondValue) {
      // Only second option selected - use the variant from the data attribute
      const variantId = selectedSecondOption.dataset.variantId;
      const variantAvailable = selectedSecondOption.dataset.variantAvailable === 'true';
      const variantPrice = selectedSecondOption.dataset.variantPrice;
      const variantComparePrice = selectedSecondOption.dataset.variantComparePrice || null;

      // Try to get variant image if available
      if (productHandle) {
        // Fetch product to get variant image
        return fetch(`/products/${productHandle}.js`)
          .then(response => response.json())
          .then(product => {
            const variant = product.variants.find(v => v.id.toString() === variantId);
            let variantImage = null;
            if (variant && variant.featured_image) {
              variantImage = variant.featured_image.src || null;
            }
            return {
              variantId: variantId,
              available: variantAvailable,
              image: variantImage,
              price: variantPrice,
              compareAtPrice: variantComparePrice
            };
          })
          .catch(() => {
            return {
              variantId: variantId,
              available: variantAvailable,
              image: null,
              price: variantPrice,
              compareAtPrice: variantComparePrice
            };
          });
      }

      return Promise.resolve({
        variantId: variantId,
        available: variantAvailable,
        image: null,
        price: variantPrice,
        compareAtPrice: variantComparePrice
      });
    }

    return Promise.resolve(null);
  }

  // Function to fetch product variants and find exact match
  function fetchProductVariants(productHandle, selectedColor, selectedSecondValue, fallbackColorImage) {
    return fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        console.log('Product data:', product);

        // Find the variant that matches both selected options
        // Normalize option values for comparison (remove brackets, trim whitespace)
        const normalizeOptionValue = (value) => {
          if (!value) return '';
          return String(value).replace(/[\[\]()]/g, '').trim().toLowerCase();
        };

        const normalizedSelectedColor = normalizeOptionValue(selectedColor);
        const normalizedSelectedSecond = normalizeOptionValue(selectedSecondValue);

        const matchingVariant = product.variants.find(variant => {
          // Check if variant options match (normalized comparison)
          const variantOptions = variant.options.map(opt => normalizeOptionValue(opt));
          const hasColor = normalizedSelectedColor ? variantOptions.includes(normalizedSelectedColor) : true;
          const hasSecondOption = normalizedSelectedSecond ? variantOptions.includes(normalizedSelectedSecond) : true;

          // Also check exact match in case normalization doesn't work
          const exactColorMatch = selectedColor ? variant.options.includes(selectedColor) : true;
          const exactSecondMatch = selectedSecondValue ? variant.options.includes(selectedSecondValue) : true;

          return (hasColor || exactColorMatch) && (hasSecondOption || exactSecondMatch);
        });

        if (matchingVariant) {
          console.log('Found matching variant:', matchingVariant);

          // Get the variant's featured image or fallback to product images
          let variantImage = fallbackColorImage; // Default fallback

          if (matchingVariant.featured_image && matchingVariant.featured_image.src) {
            // Use the variant's specific image
            variantImage = matchingVariant.featured_image.src;
          } else if (matchingVariant.featured_image) {
            // Handle different image object structures
            variantImage = matchingVariant.featured_image;
          } else {
            // Look for images in the product that might match this variant
            const variantImageObj = product.images.find(img =>
              img.variant_ids && img.variant_ids.includes(matchingVariant.id)
            );
            if (variantImageObj) {
              variantImage = variantImageObj.src;
            }
          }

          console.log('Using variant image:', variantImage);

          // Format prices using Shopify's money format
          const formatPrice = (price) => {
            // Convert cents to currency value
            const amount = price / 100;

            // Format with comma decimal separator and currency symbol before amount
            const formatted = amount.toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
            return '€' + formatted;
          };

          return {
            variantId: matchingVariant.id,
            available: matchingVariant.available,
            image: variantImage,
            price: formatPrice(matchingVariant.price),
            compareAtPrice: matchingVariant.compare_at_price ? formatPrice(matchingVariant.compare_at_price) : null
          };
        } else {
          console.log('No matching variant found, using color variant');
          // Fallback to color-only variant
          const colorVariant = product.variants.find(variant =>
            variant.options.includes(selectedColor)
          );

          if (colorVariant) {
            let colorVariantImage = fallbackColorImage;

            // Try to get the color variant's specific image
            if (colorVariant.featured_image && colorVariant.featured_image.src) {
              colorVariantImage = colorVariant.featured_image.src;
            } else if (colorVariant.featured_image) {
              colorVariantImage = colorVariant.featured_image;
            } else {
              const colorImageObj = product.images.find(img =>
                img.variant_ids && img.variant_ids.includes(colorVariant.id)
              );
              if (colorImageObj) {
                colorVariantImage = colorImageObj.src;
              }
            }

            // Format prices using Shopify's money format
            const formatPrice = (price) => {
              // Convert cents to currency value
              const amount = price / 100;

              // Format with comma decimal separator and currency symbol before amount
              const formatted = amount.toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              return '€' + formatted;
            };

            return {
              variantId: colorVariant.id,
              available: colorVariant.available,
              image: colorVariantImage,
              price: formatPrice(colorVariant.price),
              compareAtPrice: colorVariant.compare_at_price ? formatPrice(colorVariant.compare_at_price) : null
            };
          }

          return {
            variantId: null,
            available: false,
            image: fallbackColorImage
          };
        }
      })
      .catch(error => {
        console.error('Error fetching product variants:', error);
        // Fallback to color swatch data
        return {
          variantId: null,
          available: false,
          image: fallbackColorImage
        };
      });
  }

  // Function to update variant and UI
  function updateVariantAndUI(productCard, productId, variantData, colorName = null) {
    if (!variantData) {
      console.warn('updateVariantAndUI: No variant data provided');
      return;
    }

    console.log('🔄 Updating variant and UI:', {
      productId: productId,
      variantId: variantData.variantId,
      available: variantData.available
    });

    // Update product image - now considers both color and secondary option
    updateProductImage(productCard, variantData, colorName);

    // Update product price
    updateProductPrice(productCard, variantData);

    // Update product badges (sale/sold out)
    updateProductBadges(productCard, variantData);

    // Update hidden variant ID input - try multiple selectors for robustness
    let variantInput = productCard.querySelector('.product-variant-id[data-product-id="' + productId + '"]');
    if (!variantInput) {
      // Fallback: try without data-product-id attribute
      variantInput = productCard.querySelector('.product-variant-id');
    }
    if (!variantInput) {
      // Fallback: try finding input by name
      variantInput = productCard.querySelector('input[name="id"]');
    }

    if (variantInput && variantData.variantId) {
      console.log('✅ Updating variant input:', {
        oldValue: variantInput.value,
        newValue: variantData.variantId,
        input: variantInput
      });
      variantInput.value = variantData.variantId;
      variantInput.disabled = !variantData.available;
    } else {
      console.warn('⚠️ Variant input not found:', {
        productId: productId,
        variantId: variantData.variantId,
        foundInput: !!variantInput,
        allInputs: productCard.querySelectorAll('.product-variant-id'),
        allIdInputs: productCard.querySelectorAll('input[name="id"]')
      });
    }

    // Update wishlist button variant ID and state
    const wishlistButton = productCard.querySelector('.pure-wishlist-btn');
    if (wishlistButton && variantData.variantId) {
      wishlistButton.setAttribute('data-variant-id', variantData.variantId);
      console.log('✅ Updated wishlist button variant ID:', variantData.variantId);

      // Update wishlist button state based on current variant
      if (window.PureWishlistInstance) {
        const productHandle = wishlistButton.getAttribute('data-product-handle');
        const isInWishlist = window.PureWishlistInstance.isInWishlist(productHandle, variantData.variantId);

        const heartOutline = wishlistButton.querySelector('.heart-outline');
        const heartFilled = wishlistButton.querySelector('.heart-filled');

        if (isInWishlist) {
          if (heartOutline) heartOutline.style.display = 'none';
          if (heartFilled) heartFilled.style.display = 'block';
          wishlistButton.classList.add('is-active');
          wishlistButton.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          if (heartOutline) heartOutline.style.display = 'block';
          if (heartFilled) heartFilled.style.display = 'none';
          wishlistButton.classList.remove('is-active');
          wishlistButton.setAttribute('aria-label', 'Add to wishlist');
        }
      }
    }

    // Update add to cart button
    const addButton = productCard.querySelector('.collection-product-card__add-button');
    if (addButton) {
      // Update icon visibility based on availability
      // Find the icon SVG (it's rendered by kwmobile_icons snippet)
      const buttonIcon = addButton.querySelector('svg');
      if (buttonIcon) {
        if (variantData.available) {
          buttonIcon.style.display = '';
        } else {
          buttonIcon.style.display = 'none';
        }
      }

      const buttonText = addButton.querySelector('span');
      if (buttonText) {
        // Check if this is a view product button (when show_quick_add is disabled)
        const isViewButton = addButton.classList.contains('collection-product-card__view-button');

        if (isViewButton) {
          // View button: show "Ausverkauft" if sold out, otherwise "Produkt ansehen"
          if (!variantData.available) {
            buttonText.textContent = window.kwCollectionCardI18n.soldOut;
            // Disable button if variant is sold out (for anchor tags, remove href and add disabled class)
            const originalHref = addButton.getAttribute('data-original-href') || addButton.getAttribute('href');
            if (originalHref && !addButton.hasAttribute('data-original-href')) {
              addButton.setAttribute('data-original-href', originalHref);
            }
            addButton.removeAttribute('href');
            addButton.classList.add('disabled');
            addButton.style.cursor = 'not-allowed';
            addButton.style.pointerEvents = 'none';
          } else {
            buttonText.textContent = window.kwCollectionCardI18n.viewProduct;
            // Re-enable button if variant becomes available
            const originalHref = addButton.getAttribute('data-original-href');
            if (originalHref) {
              addButton.setAttribute('href', originalHref);
            } else {
              // Fallback to product URL from title link
              const productUrl = productCard.querySelector('.collection-product-card__title-link');
              if (productUrl && productUrl.getAttribute('href')) {
                addButton.setAttribute('href', productUrl.getAttribute('href'));
              }
            }
            addButton.classList.remove('disabled');
            addButton.style.cursor = '';
            addButton.style.pointerEvents = '';
          }
        } else {
          // This is an add to cart button - update text based on availability
          if (variantData.available) {
            buttonText.textContent = window.kwCollectionCardI18n.addToCart;
            addButton.disabled = false;
            addButton.dataset.processing = 'false'; // Reset processing state when variant changes
          } else {
            // When sold out, show "Ausverkauft"
            buttonText.textContent = window.kwCollectionCardI18n.soldOut;
            addButton.disabled = true;
            addButton.dataset.processing = 'false'; // Reset processing state when variant changes
          }
        }
      }
    }
  }

  // Function to update product price based on selected variant
  function updateProductPrice(productCard, variantData) {
    if (!variantData || !variantData.price) return;

    const priceContainer = productCard.querySelector('.collection-product-card__price');
    if (!priceContainer) return;

    console.log('🔄 Updating price:', {
      variantId: variantData.variantId,
      price: variantData.price,
      compareAtPrice: variantData.compareAtPrice
    });

    // Update current price
    const currentPriceElement = priceContainer.querySelector('.collection-product-card__price-current');
    if (currentPriceElement && variantData.price) {
      currentPriceElement.textContent = variantData.price;

      // Update sale class based on compare at price
      if (variantData.compareAtPrice && parseFloat(variantData.compareAtPrice.replace(/[^0-9.-]+/g, '')) > parseFloat(variantData.price.replace(/[^0-9.-]+/g, ''))) {
        currentPriceElement.classList.add('collection-product-card__price-current--sale');
      } else {
        currentPriceElement.classList.remove('collection-product-card__price-current--sale');
      }
    }

    // Update or create compare at price
    let compareAtPriceElement = priceContainer.querySelector('.collection-product-card__price-compare');

    if (variantData.compareAtPrice && parseFloat(variantData.compareAtPrice.replace(/[^0-9.-]+/g, '')) > parseFloat(variantData.price.replace(/[^0-9.-]+/g, ''))) {
      // Show compare at price
      if (!compareAtPriceElement) {
        compareAtPriceElement = document.createElement('span');
        compareAtPriceElement.className = 'collection-product-card__price-compare';
        priceContainer.insertBefore(compareAtPriceElement, currentPriceElement);
      }
      compareAtPriceElement.textContent = variantData.compareAtPrice;
      compareAtPriceElement.style.display = 'inline';
    } else {
      // Hide compare at price
      if (compareAtPriceElement) {
        compareAtPriceElement.style.display = 'none';
      }
    }
  }

  // Function to update product badges based on selected variant
  function updateProductBadges(productCard, variantData) {
    if (!variantData) return;

    const badgesContainer = productCard.querySelector('.collection-product-card__badges');
    if (!badgesContainer) return;

    const soldOutBadge = badgesContainer.querySelector('.collection-product-card__badge--sold-out');
    const saleBadge = badgesContainer.querySelector('.collection-product-card__badge--sale');

    console.log('🔄 Updating badges:', {
      variantId: variantData.variantId,
      available: variantData.available,
      price: variantData.price,
      compareAtPrice: variantData.compareAtPrice
    });

    // Update sold out badge
    if (soldOutBadge) {
      soldOutBadge.style.display = !variantData.available ? 'inline-block' : 'none';
    }

    // Update sale badge - check if variant is on sale
    if (saleBadge) {
      const isOnSale = variantData.available &&
                      variantData.compareAtPrice &&
                      parseFloat(variantData.compareAtPrice.replace(/[^0-9.-]+/g, '')) > parseFloat(variantData.price.replace(/[^0-9.-]+/g, ''));

      saleBadge.style.display = isOnSale ? 'inline-block' : 'none';

      console.log('🏷️ Sale badge update:', {
        isOnSale: isOnSale,
        display: saleBadge.style.display,
        compareAtPrice: variantData.compareAtPrice,
        price: variantData.price
      });
    }
  }

  // Function to update product image based on selected variant
  function updateProductImage(productCard, variantData, colorName = null) {
    const productImage = productCard.querySelector('.collection-product-card__image');
    if (!productImage) return;

    // Get selected options for alt text
    const selectedColorSwatch = productCard.querySelector('.collection-product-card__color-swatch.active');
    const selectedSecondOption = productCard.querySelector('.collection-product-card__second-option-value.active');
    const selectedColor = selectedColorSwatch ? selectedColorSwatch.dataset.color : null;
    const selectedSecondValue = selectedSecondOption ? selectedSecondOption.dataset.optionValue : null;

    // If we have variant data with an image, use it
    if (variantData.image && variantData.image !== '') {
      console.log('🔄 Updating image based on variant:', {
        element: productImage,
        oldSrc: productImage.src,
        newSrc: variantData.image,
        selectedColor: selectedColor,
        selectedSecondOption: selectedSecondValue,
        variantId: variantData.variantId
      });

      productImage.src = variantData.image;

      // Update alt text with both options
      const productTitle = productCard.querySelector('.collection-product-card__title').textContent;
      let altText = productTitle;
      if (selectedColor) altText += ' - ' + selectedColor;
      if (selectedSecondValue) altText += ' - ' + selectedSecondValue;
      productImage.alt = altText;

      // Update srcset for responsive images
      productImage.srcset = variantData.image.replace('_800x800', '_165x165') + ' 165w, ' +
                            variantData.image.replace('_800x800', '_360x360') + ' 360w, ' +
                            variantData.image.replace('_800x800', '_533x533') + ' 533w, ' +
                            variantData.image.replace('_800x800', '_720x720') + ' 720w, ' +
                            variantData.image + ' 800w';
    } else {
      console.log('❌ No variant image available for combination:', {
        selectedColor: selectedColor,
        selectedSecondOption: selectedSecondValue
      });
    }
  }

  // Handle color swatch clicks (only if not already handled)
  function handleSwatchClick(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    console.log('🎨 Color swatch clicked:', {
      color: this.dataset.color,
      element: this
    });

    const productId = this.closest('.collection-product-card__colors').dataset.productId;
    const colorName = this.dataset.color;

    // Find the product card container
    const productCard = this.closest('.collection-product-card');
    if (!productCard) {
      console.warn('⚠️ Product card not found for swatch click');
      return;
    }

    // Update active swatch
    productCard.querySelectorAll('.collection-product-card__color-swatch').forEach(s => s.classList.remove('active'));
    this.classList.add('active');

    // Find the correct variant based on all selected options
    const variantPromise = findVariantBySelectedOptions(productCard, productId);
    variantPromise.then(variantData => {
      updateVariantAndUI(productCard, productId, variantData, colorName);
    }).catch(error => {
      console.error('❌ Error in variant switching:', error);
    });
  }

  // Handle second option clicks
  function handleSecondOptionClick(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    console.log('📦 Second option clicked:', {
      optionValue: this.dataset.optionValue,
      element: this
    });

    const productId = this.closest('.collection-product-card__second-option').dataset.productId;
    const optionValue = this.dataset.optionValue;

    // Find the product card container
    const productCard = this.closest('.collection-product-card');
    if (!productCard) {
      console.warn('⚠️ Product card not found for second option click');
      return;
    }

    // Update active option
    productCard.querySelectorAll('.collection-product-card__second-option-value').forEach(o => o.classList.remove('active'));
    this.classList.add('active');

    // Find the correct variant based on all selected options
    const variantPromise = findVariantBySelectedOptions(productCard, productId);
    variantPromise.then(variantData => {
      updateVariantAndUI(productCard, productId, variantData);
    }).catch(error => {
      console.error('❌ Error in variant switching:', error);
    });
  }

  // ---- Idempotent card init: attaches handlers to any not-yet-bound card ----
  // ---- controls within `root` (document on first load, or document again ----
  // ---- on a debounced re-scan after Boost/sliders/quick-views inject new cards). ----
  function initCards(root) {
    root = root || document;

    // Handle second option count clicks to expand remaining options
    root.querySelectorAll('.collection-product-card__second-option-count:not([data-listener-added])').forEach(function (optionCount) {
      optionCount.setAttribute('data-listener-added', 'true');

      function expandSecondOptions() {
        const optionContainer = optionCount.closest('.collection-product-card__second-option');
        const hiddenOptions = optionContainer.querySelectorAll('.collection-product-card__second-option-value--hidden');

        // Add expanded class to show hidden options and hide count
        optionContainer.classList.add('expanded');

        // Add event listeners to newly visible options
        hiddenOptions.forEach(function (option) {
          if (!option.hasAttribute('data-listener-added')) {
            option.setAttribute('data-listener-added', 'true');
            option.addEventListener('click', handleSecondOptionClick);
          }
        });
      }

      optionCount.addEventListener('click', expandSecondOptions);
      optionCount.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          expandSecondOptions();
        }
      });
    });

    // Handle color count clicks to expand remaining swatches
    root.querySelectorAll('.collection-product-card__color-count:not([data-listener-added])').forEach(function (colorCount) {
      colorCount.setAttribute('data-listener-added', 'true');

      function expandColors() {
        const colorsContainer = colorCount.closest('.collection-product-card__colors');
        const hiddenSwatches = colorsContainer.querySelectorAll('.collection-product-card__color-swatch--hidden');

        console.log('Expanding colors:', {
          colorsContainer: colorsContainer,
          hiddenSwatches: hiddenSwatches,
          hiddenSwatchesCount: hiddenSwatches.length
        });

        // Add expanded class to show hidden swatches and hide count
        colorsContainer.classList.add('expanded');

        // Add event listeners to newly visible swatches
        hiddenSwatches.forEach(function (swatch) {
          if (!swatch.hasAttribute('data-listener-added')) {
            swatch.setAttribute('data-listener-added', 'true');
            swatch.addEventListener('click', handleSwatchClick);
          }
        });
      }

      colorCount.addEventListener('click', expandColors);
      colorCount.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          expandColors();
        }
      });
    });

    // Add event listeners to initially visible swatches
    root.querySelectorAll('.collection-product-card__color-swatch:not([data-listener-added])').forEach(function (swatch) {
      swatch.setAttribute('data-listener-added', 'true');
      swatch.addEventListener('click', handleSwatchClick);
    });

    // Add event listeners to initially visible second options
    root.querySelectorAll('.collection-product-card__second-option-value:not([data-listener-added])').forEach(function (option) {
      option.setAttribute('data-listener-added', 'true');
      option.addEventListener('click', handleSecondOptionClick);
    });
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      // The external script loads with `defer`, which already runs after the DOM
      // is parsed, so this branch is the normal path.
      fn();
    }
  }

  ready(function () {
    initCards(document);

    // Re-scan for cards added later: Boost-rendered grids, sliders, infinite
    // scroll, quick views. Debounced so a burst of DOM mutations (e.g. a whole
    // Boost grid rendering at once) triggers one re-scan, not hundreds.
    let rescanTimer = null;
    const observer = new MutationObserver(function () {
      clearTimeout(rescanTimer);
      rescanTimer = setTimeout(function () {
        initCards(document);
      }, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  // Exposed for manual re-init (e.g. an app callback after it injects cards).
  window.kwInitCollectionProductCards = initCards;

  // ---- Cart Add Confirmation Modal ----
  // Page-level singleton: guards its own modal creation and its submit
  // handler, and the submit handler is a single document-level delegated
  // listener (capture phase), so it needs no re-binding when new cards
  // appear. Runs once, independent of initCards/MutationObserver above.
  (function () {
    // Create modal HTML structure
    const modalHTML = `
    <div id="collection-cart-confirmation-modal" class="collection-cart-confirmation-modal" style="display: none;">
      <div class="collection-cart-confirmation-modal__overlay"></div>
      <div class="collection-cart-confirmation-modal__content">
        <button class="collection-cart-confirmation-modal__close" aria-label="Close">&times;</button>
        <div class="collection-cart-confirmation-modal__body">
          <div class="collection-cart-confirmation-modal__success-icon">✓</div>
          <h3 class="collection-cart-confirmation-modal__title">${window.kwCollectionCardI18n.cartSuccessTitle}</h3>
          <div class="collection-cart-confirmation-modal__product">
            <div class="collection-cart-confirmation-modal__image-container">
              <img class="collection-cart-confirmation-modal__image" src="" alt="">
            </div>
            <div class="collection-cart-confirmation-modal__details">
              <div class="collection-cart-confirmation-modal__product-title"></div>
              <div class="collection-cart-confirmation-modal__variant-details"></div>
              <div class="collection-cart-confirmation-modal__price"></div>
            </div>
          </div>
          <div class="collection-cart-confirmation-modal__actions">
            <button class="collection-cart-confirmation-modal__continue" type="button">${window.kwCollectionCardI18n.continueShopping}</button>
            <a href="/cart" class="collection-cart-confirmation-modal__view-cart">${window.kwCollectionCardI18n.viewCart}</a>
          </div>
        </div>
      </div>
    </div>
  `;

    // Insert modal into body if it doesn't exist
    if (!document.getElementById('collection-cart-confirmation-modal')) {
      document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    const modal = document.getElementById('collection-cart-confirmation-modal');
    const overlay = modal.querySelector('.collection-cart-confirmation-modal__overlay');
    const closeBtn = modal.querySelector('.collection-cart-confirmation-modal__close');
    const continueBtn = modal.querySelector('.collection-cart-confirmation-modal__continue');

    // Function to show modal
    function showCartConfirmationModal(productData) {
      const imageEl = modal.querySelector('.collection-cart-confirmation-modal__image');
      const titleEl = modal.querySelector('.collection-cart-confirmation-modal__product-title');
      const variantDetailsEl = modal.querySelector('.collection-cart-confirmation-modal__variant-details');
      const priceEl = modal.querySelector('.collection-cart-confirmation-modal__price');

      // Set image
      if (productData.image) {
        imageEl.src = productData.image;
        imageEl.alt = productData.title || 'Product';
      }

      // Set title
      titleEl.textContent = productData.title || 'Product';

      // Set variant details (stacked on separate lines)
      if (productData.variantDetails && productData.variantDetails.length > 0) {
        variantDetailsEl.textContent = productData.variantDetails.join('\n');
        variantDetailsEl.style.display = 'block';
      } else {
        variantDetailsEl.style.display = 'none';
      }

      // Set price
      if (productData.price) {
        priceEl.textContent = productData.price;
        priceEl.style.display = 'block';
      } else {
        priceEl.style.display = 'none';
      }

      // Show modal
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }

    // Function to hide modal
    function hideCartConfirmationModal() {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }

    // Close modal handlers
    closeBtn.addEventListener('click', hideCartConfirmationModal);
    continueBtn.addEventListener('click', hideCartConfirmationModal);

    // Close modal when clicking on overlay (outside content)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        hideCartConfirmationModal();
      }
    });

    // Prevent clicks on content from closing modal
    const modalContent = modal.querySelector('.collection-cart-confirmation-modal__content');
    if (modalContent) {
      modalContent.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    // Also close when clicking directly on the modal container (outside content)
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target === overlay) {
        hideCartConfirmationModal();
      }
    });

    // Escape key to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.style.display === 'block') {
        hideCartConfirmationModal();
      }
    });

    // Intercept form submissions (only add listener once)
    if (!window.collectionProductFormHandlerAdded) {
      window.collectionProductFormHandlerAdded = true;

      document.addEventListener('submit', function (e) {
        const form = e.target;
        if (!form.classList.contains('collection-product-form')) return;

        // Prevent duplicate submissions - check button state first
        const button = form.querySelector('button[type="submit"]');
        if (!button) return;

        // Prevent duplicate submissions - check if already processing
        if (button.disabled || button.dataset.processing === 'true') {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        // Prevent default and stop propagation to prevent product-form handler from firing
        // Only stop propagation for form submissions, not other events
        e.preventDefault();
        e.stopImmediatePropagation();

        const formData = new FormData(form);
        const variantId = formData.get('id');
        const productCard = form.closest('.collection-product-card');
        let productHandle = productCard ? productCard.dataset.productHandle ||
          (productCard.querySelector('.collection-product-card__colors') ?
           productCard.querySelector('.collection-product-card__colors').dataset.productHandle : null) : null;

        if (!productHandle) {
          // Fallback: try to get from product URL
          const productLink = productCard ? productCard.querySelector('.collection-product-card__title-link') : null;
          if (productLink) {
            const urlParts = productLink.href.split('/products/');
            if (urlParts.length > 1) {
              productHandle = urlParts[1].split('?')[0].split('#')[0];
            }
          }
        }

        // Mark as processing
        button.dataset.processing = 'true';
        const originalText = button.querySelector('span') ? button.querySelector('span').textContent : button.textContent;

        // Show loading state
        button.disabled = true;
        if (button.querySelector('span')) {
          button.querySelector('span').textContent = 'Adding...';
        } else {
          button.textContent = 'Adding...';
        }

        // Create new FormData with explicit quantity of 1
        const cartData = new FormData();
        cartData.append('id', variantId);
        cartData.append('quantity', '1');

        // Add to cart
        fetch('/cart/add.js', {
          method: 'POST',
          body: cartData
        })
          .then(response => response.json())
          .then(data => {
            if (data.status) {
              // Handle error
              console.error('Cart add error:', data);
              button.disabled = false;
              button.dataset.processing = 'false';
              if (button.querySelector('span')) {
                button.querySelector('span').textContent = originalText;
              } else {
                button.textContent = originalText;
              }
              alert(data.description || 'Error adding product to cart');
              return;
            }

            // Reset button
            button.disabled = false;
            button.dataset.processing = 'false';
            if (button.querySelector('span')) {
              button.querySelector('span').textContent = originalText;
            } else {
              button.textContent = originalText;
            }

            // Fetch product data to get variant details
            if (productHandle && variantId) {
              fetch(`/products/${productHandle}.js`)
                .then(response => response.json())
                .then(product => {
                  const variant = product.variants.find(v => v.id.toString() === variantId);
                  if (!variant) {
                    console.error('Variant not found');
                    return;
                  }

                  // Get variant image
                  let variantImage = product.featured_image || '';
                  if (variant.featured_image) {
                    variantImage = variant.featured_image.src || variant.featured_image;
                  } else {
                    // Try to find image associated with this variant
                    const variantImageObj = product.images.find(img =>
                      img.variant_ids && img.variant_ids.includes(variant.id)
                    );
                    if (variantImageObj) {
                      variantImage = variantImageObj.src;
                    }
                  }

                  // Format price
                  const formatPrice = (price) => {
                    const amount = price / 100;
                    const formatted = amount.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    });
                    return '€' + formatted;
                  };

                  // Translations for option names and values (from the global i18n config)
                  const translations = {
                    colorOptionName: window.kwCollectionCardI18n.colorOptionName,
                    designPrefix: window.kwCollectionCardI18n.designPrefix
                  };

                  // Format option name display (e.g., Color, color, color_1 -> translated)
                  const formatOptionName = (name) => {
                    if (!name) return name;
                    // Normalize option name (remove brackets, trim, lowercase)
                    const clean = name.replace(/[\[\]()]/g, '').trim().toLowerCase();

                    // Map common option names to translated display names
                    // Handles: Color, color, COLOR, Color_1, color_1, Colour, colour, Colour_1, colour_1, [Color], etc.
                    if (clean === 'color' || clean === 'colour' || clean === 'color_1' || clean === 'colour_1') {
                      return translations.colorOptionName || 'Color';
                    }

                    // Return original name if no mapping found
                    return name;
                  };

                  // Format option value display (e.g., [dsg05] or dsg05 -> Design 5)
                  const formatOptionValue = (value) => {
                    if (!value) return value;
                    // Remove brackets and convert to lowercase
                    const clean = value.replace(/[\[\]]/g, '').trim().toLowerCase();
                    // Check if it matches dsg pattern (e.g., dsg05, dsg5)
                    const dsgMatch = clean.match(/^dsg(\d+)$/);
                    if (dsgMatch) {
                      const designPrefix = translations.designPrefix || 'Design';
                      return `${designPrefix} ${dsgMatch[1]}`;
                    }
                    return value;
                  };

                  // Build variant details string
                  // Format: "Option1name: Blau, Option2name: 8x10"
                  const variantDetails = [];
                  if (product.options && product.options.length > 0) {
                    product.options.forEach((option, index) => {
                      const optionValue = variant.options[index];
                      if (optionValue) {
                        // Format: "OptionName: Value" (with formatted name and value)
                        const formattedName = formatOptionName(option.name);
                        const formattedValue = formatOptionValue(optionValue);
                        variantDetails.push(`${formattedName}: ${formattedValue}`);
                      }
                    });
                  }

                  // Show modal
                  showCartConfirmationModal({
                    title: product.title,
                    image: variantImage,
                    price: formatPrice(variant.price),
                    variantDetails: variantDetails
                  });
                })
                .catch(error => {
                  console.error('Error fetching product data:', error);
                  // Show modal with minimal data
                  showCartConfirmationModal({
                    title: productCard ? productCard.querySelector('.collection-product-card__title')?.textContent || 'Product' : 'Product',
                    image: '',
                    price: '',
                    variantDetails: []
                  });
                });
            } else {
              // Show modal with minimal data if we can't fetch product
              showCartConfirmationModal({
                title: productCard ? productCard.querySelector('.collection-product-card__title')?.textContent || 'Product' : 'Product',
                image: '',
                price: '',
                variantDetails: []
              });
            }

            // Update cart icon bubble
            if (window.routes && window.routes.cart_url) {
              fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
                .then((response) => response.text())
                .then((responseText) => {
                  const html = new DOMParser().parseFromString(responseText, 'text/html');
                  const cartIconBubble = document.querySelector('#cart-icon-bubble');
                  const newCartIconBubble = html.querySelector('.shopify-section');
                  if (cartIconBubble && newCartIconBubble) {
                    cartIconBubble.innerHTML = newCartIconBubble.innerHTML;
                  }
                })
                .catch((e) => {
                  console.error('Error updating cart icon:', e);
                });
            }

            // Trigger cart update event
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'collection-product-card',
                productVariantId: variantId,
                cartData: data,
              });
            }
          })
          .catch(error => {
            console.error('Error adding to cart:', error);
            button.disabled = false;
            button.dataset.processing = 'false';
            if (button.querySelector('span')) {
              button.querySelector('span').textContent = originalText;
            } else {
              button.textContent = originalText;
            }
            alert('Error adding product to cart. Please try again.');
          });
      }, true); // Use capture phase to intercept before product-form handler
    }
  })();
})();
